import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Home, Search, Phone, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  LEAD_CARD_CLASS,
  LEAD_FIELD_CLASS,
} from "../components/leadMagnetStyles";
import { trackLead } from "@/lib/analytics";
import {
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
  useLeadFormGuard,
} from "@/lib/hubspot";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

export default function NotFoundPage() {
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { companyUrl, setCompanyUrl, mountedAt } = useLeadFormGuard();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: form.email,
          name: form.name,
          message: form.message,
          source: "not_found_contact_form",
        },
        {
          companyUrl: honeypotValue(e, companyUrl),
          mountedAt,
          name: form.name,
        },
      );
      if (outcome === "sent") trackLead("not_found_contact_form");
      setSubmitted(true);
    } catch {
      setError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Page Not Found"
        description="The page you’re looking for can’t be found. Explore our services, case studies, or request a call back from the Maxiom Apps team."
        path={location.pathname}
        robots="noindex,follow"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">
                404 – Page Not Found
              </p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="not-found-heading">
                We couldn’t find that page.
              </h1>
              <p className="text-lg text-gray-400 max-w-xl mb-8">
                The URL may be incorrect or the page may have moved. Use the quick links below or tell us what you were
                looking for and we’ll help you get there.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/" data-testid="not-found-home-link">
                  <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                    <Home className="mr-2 w-5 h-5" />
                    Go to Homepage
                  </Button>
                </Link>
                <Link to="/contact" data-testid="not-found-contact-link">
                  <Button
                    variant="outline"
                    className="border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] px-8 py-6 text-base rounded-md transition-all duration-300"
                  >
                    Contact Our Team <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Smart navigation widget */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              className="bg-[#121212] border border-white/5 rounded-2xl p-6 md:p-8 space-y-6"
              data-testid="not-found-navigation-widget"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center">
                  <Search className="w-5 h-5 text-[#00AEEF]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Looking for something specific?</h2>
                  <p className="text-xs text-gray-500">
                    Jump straight to one of our most-visited pages.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-widest text-gray-500">Core Pages</p>
                  <ul className="space-y-1.5">
                    <li>
                      <Link to="/" className="text-gray-300 hover:text-[#00AEEF] transition-colors">
                        Home
                      </Link>
                    </li>
                    <li>
                      <Link to="/case-studies" className="text-gray-300 hover:text-[#00AEEF] transition-colors">
                        Case Studies
                      </Link>
                    </li>
                    <li>
                      <Link to="/process" className="text-gray-300 hover:text-[#00AEEF] transition-colors">
                        Our Process
                      </Link>
                    </li>
                    <li>
                      <Link to="/contact" className="text-gray-300 hover:text-[#00AEEF] transition-colors">
                        Contact
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-widest text-gray-500">Popular Services</p>
                  <ul className="space-y-1.5">
                    <li>
                      <Link
                        to="/services/mobile-app-development"
                        className="text-gray-300 hover:text-[#00AEEF] transition-colors"
                      >
                        Mobile App Development
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/services/web-app-development"
                        className="text-gray-300 hover:text-[#00AEEF] transition-colors"
                      >
                        Web App Development
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/services/custom-software"
                        className="text-gray-300 hover:text-[#00AEEF] transition-colors"
                      >
                        Custom Software
                      </Link>
                    </li>
                    <li>
                      <Link to="/services/ai-ml" className="text-gray-300 hover:text-[#00AEEF] transition-colors">
                        AI &amp; Machine Learning
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <Phone className="w-4 h-4 text-[#00AEEF]" />
                Prefer to talk? Call us at{" "}
                <a href="tel:7039429420" className="text-gray-200 hover:text-[#00AEEF] transition-colors">
                  703-942-9420
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lead capture on 404 */}
      <section className="pb-20 md:pb-32 bg-[#0A0A0A] border-t border-white/5" data-testid="not-found-lead-form">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Can’t find what you’re looking for?
              </h2>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-4">
                Share a few details and our team will point you in the right direction, whether that’s a specific
                service page, a relevant case study, or a quick call to walk through your project.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>• Get a curated list of relevant resources</li>
                <li>• Ask a question about our services</li>
                <li>• Request a no-pressure call back</li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
            >
              {submitted ? (
                <div className={`${LEAD_CARD_CLASS} rounded-xl p-8 text-center`}>
                  <CheckCircle className="w-10 h-10 text-[#00AEEF] mx-auto mb-3" />
                  <h3 className="text-xl font-semibold text-white mb-2">Thanks. We’ll be in touch.</h3>
                  <p className="text-gray-200 text-sm">
                    We’ll review your note and respond with the best next step or resource for your situation.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className={`${LEAD_CARD_CLASS} rounded-xl p-8 space-y-4`}
                  data-hs-do-not-collect="true"
                  aria-busy={submitting}
                >
                  <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      name="name"
                      required
                      placeholder="Full Name *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`${LEAD_FIELD_CLASS} text-sm`}
                    />
                    <Input
                      name="email"
                      type="email"
                      required
                      placeholder="Email *"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`${LEAD_FIELD_CLASS} text-sm`}
                    />
                  </div>
                  <Textarea
                    name="message"
                    required
                    rows={4}
                    placeholder="Tell us what you were trying to find or how we can help *"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${LEAD_FIELD_CLASS} text-sm resize-none`}
                  />
                  <LeadFormError message={error} testId="not-found-form-error" />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium py-5 text-sm rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
                  >
                    {submitting ? "Sending..." : "Request a Call Back"}{" "}
                    {!submitting && <Send className="ml-2 w-4 h-4" />}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

